import { createColumnHelper } from '@tanstack/react-table';

import Table from '../common/Table';

import { formatDateTime, formatPrice } from '@/components/utils/utils';
import { IHaggleEventMinimalProduct } from '@/interfaces/api/haggle-event/IHaggleEvent';
import {
	IHaggleEventSalesReport,
	ISalesReportAgreement,
} from '@/interfaces/api/haggle-event/IHaggleEventSalesReport';

const HaggleSalesReportTable = ({
	haggle,
	products,
}: {
	haggle: IHaggleEventSalesReport;
	products: IHaggleEventMinimalProduct[];
}) => {
	const columnHelper = createColumnHelper<ISalesReportAgreement>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[10px]">{info.getValue()}</div>,
			header: () => <div className="w-[10px]">ID</div>,
		}),
		columnHelper.accessor('createdAt', {
			cell: (info) => (
				<div className="w-[140px]">{formatDateTime(info.getValue() || '')}</div>
			),
			header: () => <div className="w-[140px]">Created At</div>,
		}),

		columnHelper.accessor('productId', {
			cell: (info) => {
				const product = products.find(
					(product) => product.id.toString() === info.getValue(),
				);
				return (
					<div className="w-[240px] pr-[20px] text-center">
						{`${product?.title}`}
					</div>
				);
			},
			header: () => <div className="w-[240px] text-center">PRODUCT</div>,
		}),

		columnHelper.accessor('originalPrice', {
			cell: (info) => {
				const originalPrice = info.getValue() ?? 'N/A';

				return (
					<div className="w-[125px] text-center">{`${formatPrice(
						originalPrice,
					)}`}</div>
				);
			},
			header: () => <div className="w-[125px] text-center">ORIGINAL PRICE</div>,
		}),
		columnHelper.accessor('finalPrice', {
			cell: (info) => {
				return (
					<div className="w-[100px] text-center">{`${formatPrice(
						info.getValue() ?? '0',
					)}`}</div>
				);
			},
			header: () => <div className="w-[100px] text-center">FINAL PRICE</div>,
		}),
		columnHelper.accessor('margin', {
			cell: (info) => {
				const margin = info.getValue()?.toFixed(2) ?? 0;

				return (
					<div className="w-[60px] text-center">{`${formatPrice(margin)}`}</div>
				);
			},
			header: () => <div className="w-[60px] text-center">MAX. DISCOUNT</div>,
		}),
		columnHelper.accessor('units', {
			header: () => <div className="w-[45px] text-center">UNITS</div>,
			cell: (info) => {
				const units = info.getValue() ?? 'N/A';

				return <div className="w-[45px] text-center">{`${units}`}</div>;
			},
		}),
		columnHelper.accessor('profit', {
			cell: (info) => {
				const profit = info.getValue()?.toFixed(2) ?? 0;

				return (
					<div className="w-[60px] text-center">{`${formatPrice(profit)}`}</div>
				);
			},
			header: () => <div className="w-[60px] text-center">PROFIT</div>,
		}),
		columnHelper.accessor('finalPrice', {
			cell: (info) => {
				const totalSales = info.row.original.isSold
					? info.row.original.finalPrice * info.row.original.units
					: 0;

				const fixedTotalSales = totalSales.toFixed(2);

				return (
					<div className="w-[60px] text-center">{`${formatPrice(
						fixedTotalSales,
					)}`}</div>
				);
			},
			header: () => <div className="w-[60px] text-center">TOTAL SALES</div>,
		}),
		columnHelper.accessor('isSold', {
			header: () => <div className="w-[120px] text-center">SALE MADE</div>,
			cell: (info) => {
				const isSold = info.getValue() ?? 'N/A';

				return (
					<div className="w-[120px] text-center">{`${
						isSold ? 'Yes' : 'No'
					}`}</div>
				);
			},
		}),
	];

	return (
		<div
			className="flex flex-col items-center border-t border-gray-300 w-full bg-grey-0 border rounded-md"
			data-test="store-haggles-table"
		>
			<Table<ISalesReportAgreement>
				collection={{
					data: haggle.agreements || [],
					pageNumber: 1,
					pageSize: (haggle.agreements || []).length,
					pageCount: 1,
					itemCount: (haggle.agreements || []).length,
				}}
				columns={columns}
			/>
		</div>
	);
};

export default HaggleSalesReportTable;
